import Draggable from "react-draggable";
import ModalPortal from "./ModalPortal";
import Util from "@/js/common/Util";
import React, { JSX, ReactNode, RefObject } from "react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AppContext from "../../AppContext";

interface ModalProps {
  modalOpen: boolean;
  titleEl?: JSX.Element;
  onCloseFunc?: Function;
  dim?: boolean;
  children: ReactNode;
  visible?: boolean;
  position?: { x?: number; y?: number };
  width?: number;
  height?: number;
  className?: string;

  closeOnDimClick?: boolean;
  draggable?: boolean;
}

/**
 * z-index 레이어 규칙
 * - 모달은 alert보다 아래여야 함 (alert가 모달 뒤로 깔리는 문제 방지)
 * - spinner는 최상단(필요시)
 */
const Z_INDEX = {
  MODAL_LAYER: 1000,
  // ALERT: 2000, // alert는 openAlert 쪽에서 이 이상으로 맞추면 됨
  // SPINNER: 3000,
};

const Modal = ({
  modalOpen,
  titleEl,
  onCloseFunc,
  dim = true,
  children,
  visible = true,
  position,
  width,
  height,
  className,
  closeOnDimClick = false,
  draggable = true,
}: ModalProps) => {
  const nodeRef = React.useRef<HTMLDivElement>(null);
  const modalId = "modal_" + Util.getSpecificDate("", "day", 0, "SS", "");

  if (!modalOpen || !visible) return <></>;

  const handleClose = () => {
    if (onCloseFunc) onCloseFunc();
  };

  const layerStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: Z_INDEX.MODAL_LAYER,
    pointerEvents: "none",
  };

  const overlayStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    zIndex: 0,
    background: "rgba(0,0,0,0.45)",
    backdropFilter: "blur(2px)",
    pointerEvents: "auto",
  };

  const wrapperStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    pointerEvents: "none",
  };

  const modalBoxStyle: React.CSSProperties = {
    width: width ?? "auto",
    height: height ?? "auto",
    maxWidth: "min(1400px, calc(100vw - 48px))",
    maxHeight: "calc(100vh - 37px)",
    background: "#fff",
    borderRadius: 14,
    border: "1px solid rgba(0,0,0,0.08)",
    boxShadow: "0 18px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
    pointerEvents: "auto",
    display: "flex",
    flexDirection: "column",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 18px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    background: "#fff",
  };

  const titleWrapStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    cursor: draggable ? "grab" : "default",
  };

  const closeBtnStyle: React.CSSProperties = {
    width: 36,
    height: 36,
    border: "none",
    borderRadius: 10,
    background: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const bodyStyle: React.CSSProperties = {
    padding: "5px 18px",
    overflow: "auto",
  };

  const modalContent = (
    <div
      ref={nodeRef}
      className={(className ? className : "") + " modal-modern"}
      style={{
        ...modalBoxStyle,
        ...(position ? { position: "relative" } : {}),
      }}
      role="dialog"
      aria-modal="true"
    >
      <div style={headerStyle}>
        <div id={modalId} style={titleWrapStyle}>
          {titleEl}
        </div>

        {onCloseFunc && (
          <button
            type="button"
            style={closeBtnStyle}
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          >
            <CloseOutlinedIcon style={{ fontSize: 22, color: "#999" }} />
          </button>
        )}
      </div>

      <div
        style={{
          height: 2,
          backgroundColor: AppContext.modalthemeColor,
          margin: "0 18px",
        }}
      />

      <div style={bodyStyle}>{children}</div>
    </div>
  );

  return (
    <ModalPortal>
      <div style={layerStyle}>
        {dim ? (
          <div
            style={overlayStyle}
            onClick={() => {
              if (closeOnDimClick) handleClose();
            }}
          />
        ) : null}

        <div style={wrapperStyle}>
          {draggable ? (
            <Draggable
              nodeRef={nodeRef as RefObject<HTMLDivElement>}
              handle={"#" + modalId}
              bounds="body"
              defaultPosition={{
                x: position?.x ?? 0,
                y: position?.y ?? 0,
              }}
            >
              {modalContent}
            </Draggable>
          ) : (
            modalContent
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default Modal;
